import React, { useState } from "react";
import { rewardRepo, tierRepo, walletRepo } from "../../services";
import { redeemReward, nextTierFor } from "../../services/loyalty";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { useMockCustomer } from "./useMockCustomer";
import { Badge, Button, Card, EmptyState, ProgressBar, formatDate, useAsync } from "../../components/ui";
import Icon from "../../components/ui/Icon";

export default function CustomerWallet() {
  const { orgId, toast } = useApp();
  const { tx } = useI18n();
  const { customer } = useMockCustomer(orgId);
  const [redeeming, setRedeeming] = useState("");
  const { data: wallets, reload: reloadWallets } = useAsync(() => walletRepo.all(orgId), [orgId], []);
  const { data: tiers } = useAsync(() => tierRepo.all(orgId), [orgId], []);
  const { data: rewards, reload: reloadRewards } = useAsync(() => rewardRepo.all(orgId), [orgId], []);

  const wallet = wallets?.find((row) => row.customerId === customer?.id) || { balance: 0, points: customer?.points || 0, coupons: [] };

  const redeem = async (reward) => {
    if (!customer || redeeming) return;
    if (!window.confirm(tx(`แลก "${reward.name}" ด้วย ${reward.points} แต้ม?`, `Redeem "${reward.name}" for ${reward.points} pts?`))) return;
    setRedeeming(reward.id);
    try {
      await redeemReward({ orgId, customerId: customer.id, rewardId: reward.id });
      reloadWallets();
      reloadRewards();
      toast(tx("แลกของรางวัลสำเร็จ คูปองอยู่ในกระเป๋าของคุณแล้ว", "Redeemed — the coupon is in your wallet"), "success");
    } catch (error) {
      toast(error?.message || tx("แลกของรางวัลไม่สำเร็จ", "Redeem failed"), "error");
    } finally {
      setRedeeming("");
    }
  };
  const currentTier = tiers?.find((tier) => tier.key === customer?.tier);
  const nextTier = nextTierFor(tiers || [], customer?.totalSpend || 0);

  return (
    <>
      <Card style={{ background: "linear-gradient(135deg, var(--c-indigo-600), var(--c-violet))", color: "#fff", border: "none" }}>
        <div className="u-fs-13" style={{ opacity: 0.85 }}>{tx("แต้มสะสมของฉัน", "My points")}</div>
        <div style={{ fontSize: 32, fontWeight: 900 }}>{wallet.points} pts</div>
        <div className="u-between u-mt-8">
          <Badge tone="gray" style={{ background: "rgba(255,255,255,.2)", color: "#fff" }}>{currentTier?.name || customer?.tier}</Badge>
          <span className="u-fs-12" style={{ opacity: 0.85 }}>{tx("ยอดใช้จ่ายสะสม", "Lifetime spend")} ฿{(customer?.totalSpend || 0).toLocaleString()}</span>
        </div>
      </Card>

      {nextTier ? (
        <Card title={tx(`อีก ฿${(nextTier.minSpend - (customer?.totalSpend || 0)).toLocaleString()} จะได้เลื่อนระดับ`, `฿${(nextTier.minSpend - (customer?.totalSpend || 0)).toLocaleString()} to reach ${nextTier.name}`)}>
          <ProgressBar percent={((customer?.totalSpend || 0) / nextTier.minSpend) * 100} color={nextTier.color} />
        </Card>
      ) : null}

      <Card title={tx("คูปองของฉัน", "My coupons")}>
        {!wallet.coupons?.length ? (
          <EmptyState icon={<Icon name="ticket" />} text={tx("ยังไม่มีคูปอง", "No coupons yet")} />
        ) : (
          wallet.coupons.map((coupon) => (
            <div key={coupon.id} className="u-between" style={{ padding: "9px 0", borderBottom: "1px solid var(--c-slate-100)" }}>
              <div>
                <div className="u-strong u-fs-13"><Icon name="ticket" /> {coupon.name}</div>
                <div className="u-fs-12 u-muted">{tx("หมดอายุ", "Expires")} {formatDate(coupon.expireAt)}</div>
              </div>
            </div>
          ))
        )}
      </Card>

      <Card title={tx("แลกของรางวัล", "Redeem rewards")}>
        {!(rewards || []).length ? <EmptyState text={tx("ยังไม่มีของรางวัล", "No rewards yet")} /> : null}
        {(rewards || []).map((reward) => (
          <div key={reward.id} className="u-between" style={{ padding: "9px 0", borderBottom: "1px solid var(--c-slate-100)" }}>
            <div>
              <div className="u-strong u-fs-13">{reward.name}</div>
              <div className="u-fs-12 u-indigo">{reward.points} pts</div>
            </div>
            <Button size="sm" disabled={!customer || !!redeeming || wallet.points < reward.points || Number(reward.stock) < 1} onClick={() => redeem(reward)}>
              {Number(reward.stock) < 1 ? tx("หมด", "Out of stock") : tx("แลก", "Redeem")}
            </Button>
          </div>
        ))}
      </Card>
    </>
  );
}
